import { ApiError, Auth } from '@/services/auth-service'
import type { SessionInfo } from '@rh/shared/services/auth-service'
import { apiRequest } from '@/lib/api-client'
import { Button, Notice, PasswordInput } from '@tempots/beatui'
import { attr, ForEach, html, on, prop, When } from '@tempots/dom'

function describeUserAgent(userAgent: string | null): string {
  if (!userAgent) return 'Unknown device'
  const browser = /Edg\//.test(userAgent)
    ? 'Edge'
    : /OPR\//.test(userAgent)
      ? 'Opera'
      : /Chrome\//.test(userAgent)
        ? 'Chrome'
        : /Firefox\//.test(userAgent)
          ? 'Firefox'
          : /Safari\//.test(userAgent)
            ? 'Safari'
            : 'Browser'
  const os = /Windows/.test(userAgent)
    ? 'Windows'
    : /Mac OS X|Macintosh/.test(userAgent)
      ? 'macOS'
      : /iPhone|iPad/.test(userAgent)
        ? 'iOS'
        : /Android/.test(userAgent)
          ? 'Android'
          : /Linux/.test(userAgent)
            ? 'Linux'
            : 'unknown OS'
  return `${browser} on ${os}`
}

function messageFrom(error: unknown, fallback: string): string {
  return error instanceof ApiError && error.message ? error.message : fallback
}

/**
 * The security tail of the /profile Account box: active sessions
 * (list + revoke), personal data export, and account deletion. The
 * destructive action is deliberately quiet — a footnote, not a banner.
 */
export function AccountSecurity() {
  // --- Active sessions ----------------------------------------------------
  const sessions = prop<SessionInfo[] | null>(null)
  const sessionsError = prop<string | null>(null)
  const sessionsBusy = prop(false)

  const loadSessions = () => {
    sessionsError.set(null)
    Auth.listSessions()
      .then(list => sessions.set(list))
      .catch(error =>
        sessionsError.set(
          messageFrom(error, 'Could not load your active sessions.')
        )
      )
  }
  loadSessions()

  const revoke = (action: () => Promise<void>) => {
    sessionsBusy.set(true)
    sessionsError.set(null)
    action()
      .then(() => loadSessions())
      .catch(error =>
        sessionsError.set(
          messageFrom(error, 'Could not sign out that session.')
        )
      )
      .finally(() => sessionsBusy.set(false))
  }

  const otherCount = sessions.map(list =>
    list == null ? 0 : list.filter(s => !s.isCurrent).length
  )

  // --- Data export ----------------------------------------------------------
  const exportBusy = prop(false)
  const exportError = prop<string | null>(null)
  const handleExport = () => {
    exportBusy.set(true)
    exportError.set(null)
    apiRequest<unknown>('/users/me/export', { method: 'GET' })
      .then(data => {
        const blob = new Blob([JSON.stringify(data, null, 2)], {
          type: 'application/json',
        })
        const url = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = 'resumeheaders-data-export.json'
        anchor.click()
        URL.revokeObjectURL(url)
      })
      .catch(error =>
        exportError.set(messageFrom(error, 'Could not export your data.'))
      )
      .finally(() => exportBusy.set(false))
  }

  // --- Account deletion -----------------------------------------------------
  const deleteOpen = prop(false)
  const deletePassword = prop('')
  const deleteBusy = prop(false)
  const deleteError = prop<string | null>(null)

  const handleDelete = () => {
    if (deletePassword.value.trim() === '') {
      deleteError.set('Enter your password to confirm.')
      return
    }
    if (
      !window.confirm(
        'Permanently delete your ResumeHeaders account? This cannot be undone. Agencies where you are the only member will be deleted too.'
      )
    ) {
      return
    }
    deleteBusy.set(true)
    deleteError.set(null)
    Auth.deleteAccount(deletePassword.value)
      .then(() => {
        window.location.href = '/'
      })
      .catch(error => {
        deleteBusy.set(false)
        deleteError.set(messageFrom(error, 'Account deletion failed.'))
      })
  }

  return html.div(
    attr.class('mt-8 pt-6 border-t border-gray-200 flex flex-col gap-6'),

    // Active sessions
    html.div(
      html.h3(
        attr.class('text-sm font-semibold text-gray-700'),
        'Active Sessions'
      ),
      html.p(
        attr.class('text-xs text-gray-500 mt-0.5 mb-3'),
        'Devices currently signed in to your account.'
      ),
      When(
        sessionsError.map(e => e != null),
        () =>
          Notice(
            { variant: 'danger' },
            attr.class('mb-3'),
            sessionsError.map(e => e ?? '')
          )
      ),
      When(
        sessions.map(list => list == null),
        () => html.div(attr.class('text-xs text-gray-400'), 'Loading…'),
        () =>
          html.div(
            attr.class('flex flex-col gap-2'),
            ForEach(
              sessions.map(list => list ?? []),
              session =>
                html.div(
                  attr.class(
                    'flex items-center justify-between gap-3 rounded border border-gray-200 bg-white px-3 py-2'
                  ),
                  html.div(
                    attr.class('min-w-0'),
                    html.div(
                      attr.class('text-sm text-gray-800'),
                      attr.title(session.map(s => s.userAgent ?? '')),
                      session.map(s => describeUserAgent(s.userAgent)),
                      When(
                        session.map(s => s.isCurrent),
                        () =>
                          html.span(
                            attr.class(
                              'ml-2 text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded px-1.5 py-0.5'
                            ),
                            'This device'
                          )
                      )
                    ),
                    html.div(
                      attr.class('text-xs text-gray-500'),
                      session.map(s => {
                        const signedIn = new Date(s.createdAt).toLocaleString()
                        return s.ipAddress && s.ipAddress !== 'unknown-ip'
                          ? `Signed in ${signedIn} · ${s.ipAddress}`
                          : `Signed in ${signedIn}`
                      })
                    )
                  ),
                  When(
                    session.map(s => !s.isCurrent),
                    () =>
                      Button(
                        {
                          variant: 'outline',
                          size: 'sm',
                          disabled: sessionsBusy,
                          onClick: () =>
                            revoke(() =>
                              Auth.revokeSession(session.value.token)
                            ),
                        },
                        html.span(attr.class('whitespace-nowrap'), 'Sign out')
                      )
                  )
                )
            ),
            When(
              otherCount.map(count => count > 0),
              () =>
                html.div(
                  Button(
                    {
                      variant: 'outline',
                      size: 'sm',
                      disabled: sessionsBusy,
                      onClick: () => revoke(() => Auth.revokeOtherSessions()),
                    },
                    'Sign out all other devices'
                  )
                )
            )
          )
      )
    ),

    // Data export
    html.div(
      html.h3(attr.class('text-sm font-semibold text-gray-700'), 'Your Data'),
      html.p(
        attr.class('text-xs text-gray-500 mt-0.5 mb-2'),
        'Download a copy of the personal data stored with your account (JSON).'
      ),
      When(
        exportError.map(e => e != null),
        () =>
          Notice(
            { variant: 'danger' },
            attr.class('mb-2'),
            exportError.map(e => e ?? '')
          )
      ),
      Button(
        {
          variant: 'outline',
          size: 'sm',
          loading: exportBusy,
          disabled: exportBusy,
          onClick: handleExport,
        },
        'Download my data'
      )
    ),

    // Account deletion — quiet by design
    html.div(
      When(
        deleteOpen.map(open => !open),
        () =>
          html.p(
            attr.class('text-xs text-gray-400'),
            'Done with ResumeHeaders? ',
            html.button(
              attr.type('button'),
              attr.class('underline hover:text-gray-600'),
              on.click(() => deleteOpen.set(true)),
              'Delete your account'
            ),
            '.'
          ),
        () =>
          html.div(
            attr.class(
              'rounded border border-red-200 bg-white p-3 flex flex-col gap-2'
            ),
            html.p(
              attr.class('text-xs text-gray-600'),
              'Deleting your account removes your sign-in, profile, and agency memberships permanently. Agencies where you are the only member are deleted too. Enter your password to confirm.'
            ),
            When(
              deleteError.map(e => e != null),
              () =>
                Notice(
                  { variant: 'danger' },
                  deleteError.map(e => e ?? '')
                )
            ),
            html.div(
              attr.class('flex flex-wrap items-center gap-2'),
              html.div(
                attr.class('w-56'),
                PasswordInput({
                  value: deletePassword,
                  placeholder: 'Current password',
                  onInput: v => deletePassword.set(v),
                })
              ),
              Button(
                {
                  variant: 'filled',
                  color: 'danger',
                  size: 'sm',
                  loading: deleteBusy,
                  disabled: deleteBusy,
                  onClick: handleDelete,
                },
                'Permanently delete'
              ),
              Button(
                {
                  variant: 'text',
                  size: 'sm',
                  disabled: deleteBusy,
                  onClick: () => {
                    deleteOpen.set(false)
                    deletePassword.set('')
                    deleteError.set(null)
                  },
                },
                'Cancel'
              )
            )
          )
      )
    )
  )
}
